import { Stack, Paper, Typography, CircularProgress } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart, ChartDataSourceEnum, ChartTickesFormatEnum } from "../../models/company.model";
import { ReportDataModel, ReportModel } from "../../models/report.model";
import { chartHeight, parseChartExpression, useGroupBy } from "../../chart-helpers/group.table.helper";

export default function CustomizedLine(props: { chart: Chart; reports?: ReportDataModel }) {
  const { groupBy } = props.chart || {};
  return (
    <>
      {!groupBy && <CommonLine chart={props.chart} reports={props.reports} />}
      {groupBy && <GroupByLine chart={props.chart} reports={props.reports} />}
    </>
  );
}

function GroupByLine(props: { chart: Chart; reports?: ReportDataModel }) {
  const { loading, groups, groupLabels } = useGroupBy(props);

  return (
    <CommonLine
      chart={props.chart}
      reports={{
        main: {} as ReportModel,
        ranges: groupLabels,
        dates: groups[0].map((r) => ({
          ...r,
          sk: r.groupLabel || "",
        })) as ReportModel[],
      }}
      loading={loading}
    />
  );
}

function CommonLine(props: { chart: Chart; reports?: ReportDataModel; loading?: boolean }) {
  const { label, data, line } = props.chart || {};
  const dataSource = line?.dataSource || ChartDataSourceEnum.Main;
  const chartLabels =
    dataSource === ChartDataSourceEnum.Main ? (data || []).map((d) => d.label) : props.reports?.ranges.map((d) => d) || [];
  const datasets =
    dataSource === ChartDataSourceEnum.Main
      ? [
          {
            labels: (data || []).map((d) => d.label),
            data: (data || []).map((d) => parseChartExpression(props.reports?.[dataSource], d.expression, d.decimalPlaces)),
            backgroundColor: (data || []).map((d) => d.color),
            borderColor: (data || []).map((d) => d.color),
          },
        ]
      : (data || []).map((d) => ({
          label: d.label,
          data: (chartLabels || []).map((label) =>
            parseChartExpression(
              props.reports?.[dataSource]?.find((d) => d.sk === label),
              d.expression,
              d.decimalPlaces
            )
          ),
          backgroundColor: d.color,
          borderColor: d.color,
        }));
  const tickesFormat = line?.tickesFormat || ChartTickesFormatEnum.Decimal;
  const formatMax = tickesFormat === ChartTickesFormatEnum.Percent ? 1 : Math.max(...(datasets || []).map((d) => d.data).flat()) + 5;

  return (
    <Stack spacing={2}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-normal">
        <div style={{ height: chartHeight }}>
          {!props.loading && (
            <Line
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: dataSource !== ChartDataSourceEnum.Main,
                    position: "right",
                    labels: {
                      padding: 20,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label(tooltipItem) {
                        const index = dataSource === ChartDataSourceEnum.Main ? tooltipItem.dataIndex : tooltipItem.datasetIndex
                        const decimalPlaces = data[index]?.decimalPlaces || 0
                        if (tickesFormat === ChartTickesFormatEnum.Currency) {
                          if (typeof tooltipItem.raw === 'number') {
                            return `${tooltipItem.raw.toLocaleString("en-US", {
                              minimumFractionDigits: decimalPlaces,
                              maximumFractionDigits: decimalPlaces,
                              style: 'currency',
                              currency: 'USD',
                            })}`
                          } else {
                            return tooltipItem.formattedValue
                          }
                        }
                        if (tickesFormat === ChartTickesFormatEnum.Percent) {
                          if (typeof tooltipItem.raw === 'number') {
                            return tooltipItem.raw.toLocaleString("en-US", {
                              minimumFractionDigits: decimalPlaces,
                              maximumFractionDigits: decimalPlaces,
                              style: 'percent',
                            })
                          } else {
                            return tooltipItem.formattedValue
                          }
                        }
                        const formattedValue = typeof tooltipItem.raw === 'number' ? tooltipItem.raw.toFixed(decimalPlaces) : tooltipItem.formattedValue
                        return `${formattedValue}`
                      },
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    border: {
                      display: false,
                    },
                  },
                  y: {
                    display: true,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      format: {
                        style: tickesFormat,
                        ...(tickesFormat === ChartTickesFormatEnum.Currency && {
                          currency: "USD",
                        }),
                      },
                    },
                    max: formatMax,
                  },
                },
                elements: {
                  line: {
                    borderWidth: 6,
                  },
                },
              }}
              data={{
                labels: chartLabels,
                datasets,
              }}
              key={`${dataSource}${tickesFormat}`}
            />
          )}
          {props?.loading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </div>
      </Paper>
    </Stack>
  );
}
